<script setup>
import { useI18n } from 'vue-i18n';
import UiBadge from '@/components/ui/UiBadge.vue';

const { t } = useI18n();

/**
 * The sign-in, forgot-password and reset-password pages in the home page's
 * own language: its header, the gold glow behind the hero, a two-tone
 * headline on the left and the form in a card like "Ajet dana" on the right.
 * The block is at most 1100px wide and centred, so a wide screen keeps it
 * all within one glance.
 */
defineProps({
  badge: { type: String, default: 'Deenly panel' },
  title: { type: String, required: true },
  accent: { type: String, default: '' },
  lead: { type: String, default: '' },
  cardTitle: { type: String, required: true },
  cardHint: { type: String, default: '' },
  // Where "back" leads: the site, or the Academy for its own pages.
  home: { type: String, default: '/' },
  homeLabel: { type: String, default: 'Na sajt' },
});

const logoUrl = new URL('../../assets/deenly_logo_yellow.svg', import.meta.url).href;
</script>

<template>
  <div class="min-h-screen w-full bg-deen-mist text-deen-ink flex flex-col">
    <header class="glass-nav border-b border-deen-ink/5">
      <div class="max-w-[1100px] 4xl:max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <RouterLink to="/" class="flex items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deen-gold">
          <span class="w-10 h-10 rounded-xl bg-deen-ink flex items-center justify-center shadow-lg p-1">
            <img :src="logoUrl" alt="" class="w-full h-full object-contain" />
          </span>
          <span class="text-xl font-serif font-semibold tracking-tight">Deenly</span>
        </RouterLink>
        <RouterLink
          :to="home"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold border border-deen-ink/15 hover:bg-deen-ink hover:text-white hover:border-deen-ink transition-all shadow-sm"
        >
          <iconify-icon icon="solar:arrow-left-linear" aria-hidden="true" />{{ homeLabel }}
        </RouterLink>
      </div>
    </header>

    <main class="relative flex-1 flex items-center px-6 py-12 lg:py-16">
      <div class="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div class="absolute top-10 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-deen-gold/20 blur-[120px] rounded-full" />
      </div>

      <div class="relative w-full max-w-[1100px] 4xl:max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div v-reveal class="lg:col-span-6 flex flex-col items-start">
          <UiBadge tone="ink" class="mb-6">
            <span class="w-2 h-2 rounded-full bg-deen-gold" aria-hidden="true" />{{ badge }}
          </UiBadge>
          <h1 class="text-4xl sm:text-5xl lg:text-6xl 4xl:text-7xl font-display font-semibold leading-[1.15] tracking-tight">
            {{ title }}
            <template v-if="accent"><br /><span class="text-gradient">{{ accent }}</span></template>
          </h1>
          <p v-if="lead" class="mt-6 text-lg 4xl:text-xl text-deen-ink/60 font-medium leading-relaxed max-w-md">{{ lead }}</p>
          <slot name="aside" />
        </div>

        <div v-reveal="{ delayMs: 150 }" class="lg:col-span-6 flex lg:justify-end">
          <section class="relative w-full max-w-lg 4xl:max-w-xl rounded-[32px] bg-white border border-deen-ink/5 shadow-[0_30px_80px_-30px_rgba(28,72,72,0.35)] p-7 sm:p-9 4xl:p-12 overflow-hidden">
            <div class="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-deen-gold/25 blur-2xl pointer-events-none" aria-hidden="true" />
            <header class="relative mb-7">
              <p class="text-xs font-bold uppercase tracking-widest text-deen-ink/40">{{ cardTitle }}</p>
              <p v-if="cardHint" class="mt-2 text-sm text-deen-ink/55 leading-relaxed">{{ cardHint }}</p>
            </header>
            <div class="relative">
              <slot />
            </div>
          </section>
        </div>
      </div>
    </main>

    <footer class="px-6 pb-8 pt-2 flex justify-center gap-6 text-xs text-deen-ink/40">
      <span>© Deenly</span>
      <RouterLink to="/privacy" class="hover:text-deen-ink transition-colors">{{ t('study.password.privacy') }}</RouterLink>
    </footer>
  </div>
</template>
